export const agenticDevNL = {
  seo: {
    title: "Intent-Driven Development (IDD) voor Agentic Coding | pAIoneer Labs GmbH",
    description: "Intent-Driven Development maakt AI-ondersteunde softwareontwikkeling bestuurbaar: betekenis, beslissingen en scenario's worden operationele artefacten voor Agentic Coding.",
    keywords: "Intent-Driven Development, Intent Driven Development, IDD, Agentic Coding, agentische softwareontwikkeling, AI-ondersteunde softwareontwikkeling, Vibe Coding, Spec-Driven Development, Prompt Engineering, requirements engineering met AI",
    ogTitle: "Intent-Driven Development: betekenis vóór implementatie",
    ogDescription: "IDD operationaliseert context voordat er code ontstaat: Intent, Decisions en Scenarios worden het projectgeheugen voor agentische softwareontwikkeling.",
    twitterTitle: "Intent-Driven Development (IDD) voor Agentic Coding",
    twitterDescription: "Bestuurbare code in plaats van promptchaos: IDD maakt van betekenis, beslissingen en scenario's operationele artefacten.",
    url: "https://paioneer-labs.eu/agenticdev",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Intent-Driven Development (IDD) voor Agentic Coding",
      serviceType: "Agentische softwareontwikkeling en AI-ondersteunde ontwikkelprocessen",
      description: "Advies en implementatie voor Intent-Driven Development, IDD Vaults, workflows voor Agentic Coding en verifieerbare AI-ondersteunde softwareontwikkeling.",
      provider: {
        "@type": "Organization",
        name: "pAIoneer Labs GmbH",
        url: "https://paioneer-labs.eu"
      },
      areaServed: "DE, NL, EU",
      url: "https://paioneer-labs.eu/agenticdev"
    }
  },
  hero: {
    eyebrow: "Agentic Coding heeft expliciete context nodig",
    title: "Intent-Driven Development",
    claim: "Betekenis vóór implementatie.",
    lead: "AI schrijft code. IDD zorgt ervoor dat ze aan het juiste probleem werkt. Intent-Driven Development maakt van doelen, grenzen, beslissingen en scenario's operationele artefacten voor agentische softwareontwikkeling."
  },
  implicitContextProblem: {
    title: "Het risico is impliciete context",
    intro: "Het werkelijke risico bij AI-ondersteunde softwareontwikkeling is niet ontbrekende code. Het risico is context die verborgen blijft in iemands hoofd, in een chatgeschiedenis of in verspreide tickets.",
    items: [
      {
        title: "Onduidelijke opdracht",
        description: "Prompts beschrijven vaak een oplossing, maar niet het doel, de niet-doelen, de succescriteria en de aannames. De AI werkt dan snel, maar niet noodzakelijk in de juiste richting."
      },
      {
        title: "Toevallige architectuur",
        description: "Technologiekeuzes ontstaan terloops: gegevensopslag, modelkeuze, foutsituaties en beheer worden impliciet beslist in plaats van bewust gestuurd."
      },
      {
        title: "Niet-verifieerbaar resultaat",
        description: "Zonder scenario's en acceptatiecriteria blijft onduidelijk of de code werkelijk juist reageert of er alleen plausibel uitziet."
      }
    ],
    resolution: "IDD lost precies dit probleem op: eerst wordt betekenis geoperationaliseerd, daarna wordt de code eruit afgeleid."
  },
  antiVibeCoding: {
    title: "Vibe Coding is geen ontwikkelproces",
    description: "Voor demo's kan onderbuikgevoel volstaan. Voor software met beheer, onderhoud, beveiliging, kosten en verantwoordelijkheid volstaat het niet. Agenten hebben niet meer sfeer nodig, maar expliciete context.",
    contrasts: [
      "Vibe Coding zorgt voor beweging. IDD zorgt voor richting.",
      "Prompting levert antwoorden op. IDD levert projectgeheugen op.",
      "Agentic Coding zonder IDD blijft activiteit. Agentic Coding met IDD wordt bestuurbare ontwikkeling."
    ]
  },
  principle: {
    title: "Documentatie is geen bijproduct",
    thesis: "In IDD is documentatie een operationeel artefact.",
    description: "Documentatie is geen verslaglegging. Ze is de werkruimte waarin doelen, grenzen, beslissingen en verwacht gedrag expliciet worden voordat agenten implementeren.",
    pillarsTitle: "Intent. Decisions. Scenarios. Code.",
    pillarsIntro: "IDD reduceert softwareontwikkeling niet tot prompts. Het maakt de betekenis van een project expliciet en leidt daaruit een verifieerbare implementatie af.",
    pillars: [
      {
        title: "Intent",
        description: "Wat moet worden bereikt, wat valt er uitdrukkelijk buiten en waaraan herkennen we succes?"
      },
      {
        title: "Decisions",
        description: "Welke belangrijke beslissingen zijn genomen, in welke context en met welke gevolgen?"
      },
      {
        title: "Scenarios",
        description: "Welk gedrag wordt verwacht in normale situaties, randgevallen, foutsituaties en acceptatiesituaties?"
      },
      {
        title: "Code",
        description: "De implementatie ontstaat uit gedocumenteerde betekenis en wordt getoetst aan scenario's en controles."
      }
    ]
  },
  vault: {
    title: "De IDD Vault: projectgeheugen in plaats van promptgeschiedenis",
    description: "De IDD Vault bewaart de relevante context op de plek waar agenten en mensen die opnieuw kunnen gebruiken. Niet meer bestanden dan nodig. Elk bestand heeft een duidelijke functie.",
    rule: "Weinig artefacten. Duidelijke functie. Duurzame context.",
    effect: "De Vault voorkomt dat beslissende context verdwijnt in een chat, in iemands hoofd of in verspreide tickets.",
    treeLines: [
      "/project",
      "  /idd_vault",
      "    intent.md       # doel, niet-doelen, succescriteria, aannames",
      "    decisions.md    # beslissingen met context, onderbouwing, gevolgen",
      "    scenarios.md    # gedrag, randgevallen, acceptatiecriteria",
      "  /code",
      "  AGENTS.md         # regels en verwachtingen voor code-agenten",
      "  README.md         # startpunt, setup, bedrijfsmodus, grenzen"
    ]
  },
  outputs: {
    title: "Geen tekst. Bestuurbare code.",
    description: "De IDD-bestanden zijn geen verslag. Ze zijn input voor concreet implementatiewerk en voor het terugkoppelen van inzichten naar het project.",
    items: [
      {
        file: "intent.md",
        result: "Scope",
        description: "Endpoints, datamodel, succescriteria, niet-doelen en aannames worden zichtbaar als kader voor de ontwikkeling."
      },
      {
        file: "decisions.md",
        result: "Architectuur",
        description: "Technologie, beheer, persistentie, modelkeuze en afwegingen worden onderbouwd in plaats van terloops beslist."
      },
      {
        file: "scenarios.md",
        result: "Tests",
        description: "Acceptatietests, foutsituaties, statusovergangen en randgevallen ontstaan vóór of tijdens de implementatie."
      },
      {
        file: "AGENTS.md",
        result: "Codekwaliteit",
        description: "Regels, patronen, randvoorwaarden, migraties, OpenAPI en verwachte uitvoer worden expliciet voor agenten."
      },
      {
        file: "README.md",
        result: "Startpunt",
        description: "Setup, bedrijfsmodus, grenzen en voorbeeldworkflows blijven begrijpelijk voor mensen en agenten."
      },
      {
        file: "Code",
        result: "Terugkoppeling",
        description: "Inzichten uit implementatie en verificatie vloeien terug naar de Vault, de tests en de volgende iteraties."
      }
    ]
  },
  comparison: {
    title: "IDD begint vóór de specificatie",
    intro: "Spec-Driven Development is sterk wanneer het doel technisch al precies en stabiel is. IDD begint eerder: daar waar domein, aannames, afwegingen en foutsituaties nog expliciet gemaakt moeten worden.",
    columns: ["Aanpak", "Kern", "Sterkte", "Grens"],
    rows: [
      {
        approach: "Prompting",
        center: "Invoer voor een model",
        strength: "Snel en direct",
        limit: "Vluchtig, onvolledig en moeilijk te verifiëren"
      },
      {
        approach: "Vibe Coding",
        center: "Gevoel en iteratie",
        strength: "Nuttig voor proeftuinen en demo's",
        limit: "Geen betrouwbare sturing voor software waarvoor verantwoording moet worden afgelegd"
      },
      {
        approach: "Spec-Driven Development",
        center: "Technisch contract",
        strength: "Consistentie met een al gedefinieerde specificatie",
        limit: "Het verkeerde probleem kan formeel correct worden geïmplementeerd"
      },
      {
        approach: "Intent-Driven Development",
        center: "Betekenis, doel en beslissingen",
        strength: "Verheldert context, afwegingen en grenzen vóór de implementatie",
        limit: "Vraagt om consequent onderhoud van de context en om validatie"
      }
    ],
    summary: "Specificaties zeggen wat technisch moet gelden. IDD onderbouwt waarom precies deze richting moet worden gebouwd."
  },
  workflow: {
    title: "Verhelderen. Beslissen. Formuleren. Bouwen. Terugkoppelen.",
    description: "IDD is geen waterval. IDD is kleine, gecontroleerde iteratie met duurzame context.",
    steps: [
      {
        title: "Intent verhelderen",
        description: "Doelen, niet-doelen, succescriteria en aannames worden zichtbaar voordat er code ontstaat."
      },
      {
        title: "Beslissingen documenteren",
        description: "Architectuur- en productbeslissingen worden vastgelegd met context, onderbouwing en gevolgen."
      },
      {
        title: "Scenario's formuleren",
        description: "Normaal gedrag, randgevallen, foutsituaties en acceptatiecriteria worden verifieerbaar beschreven."
      },
      {
        title: "Agenten laten implementeren",
        description: "AI-agenten werken vanuit expliciete context in plaats van vanuit geïsoleerde prompts."
      },
      {
        title: "Resultaat controleren en terugkoppelen",
        description: "Inzichten uit code, tests en beheer werken de Vault en de volgende iteratie bij."
      }
    ]
  },
  impact: {
    title: "Waarom dit telt in echte projecten",
    description: "IDD laat AI-ondersteunde ontwikkeling aansluiten op teams, beheer en kwaliteitsborging.",
    items: [
      "Minder herstelwerk, omdat doelen en niet-doelen zichtbaar zijn.",
      "Betere architectuur, omdat beslissingen worden onderbouwd.",
      "Betere tests, omdat scenario's vóór de code bestaan.",
      "Betere samenwerking, omdat context niet in een chat verdwijnt.",
      "Betere resultaten van agenten, omdat elke stap op expliciete artefacten is gebaseerd."
    ]
  },
  contact: {
    title: "IDD in agentische ontwikkelprocessen brengen",
    description: "Als AI-ondersteunde ontwikkeling meer moet zijn dan experimenteren, heeft ze een operationeel contextmodel nodig.",
    details: "Wij helpen bij het opbouwen van IDD Vaults, agentregels, scenario's en verifieerbare ontwikkelworkflows.",
    button: "IDD bespreken",
    subject: "Intent-Driven Development bespreken",
    email: "info@paioneer.eu"
  }
};
